import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CLUSTER_LABELS, ENTITIES, type ClusterKey } from '../data';

type Mode = 'plain' | 'tech';

export function Concept() {
  const { id = '' } = useParams<{ id: string }>();
  const entity = ENTITIES[id];
  const [mode, setMode] = useState<Mode>('plain');

  useEffect(() => {
    window.scrollTo(0, 0);
    setMode('plain');
    document.title = ENTITIES[id]
      ? `${ENTITIES[id].name} — Revvly Docs`
      : 'Revvly Docs';
  }, [id]);

  const peers = useMemo(() => {
    if (!entity) return [] as string[];
    const text = (entity.connects.join(' ') + ' ' + entity.plain).toLowerCase();
    return Object.keys(ENTITIES)
      .filter((k) => k !== entity.id && ENTITIES[k].cluster !== 'root')
      .filter((pid) => {
        const name = ENTITIES[pid].name.toLowerCase();
        return text.includes(name) || text.includes(name + 's');
      })
      .slice(0, 6);
  }, [entity]);

  if (!entity) {
    return (
      <div className="tn-page">
        <div className="dp-404">
          <span className="rv-eyebrow-mini">404</span>
          <h1>We couldn&rsquo;t find that concept.</h1>
          <p>
            Try the <Link to="/docs">overview</Link>.
          </p>
        </div>
      </div>
    );
  }

  const clusterLabel = CLUSTER_LABELS[entity.cluster as ClusterKey];
  const leftPeers = peers.slice(0, Math.ceil(peers.length / 2));
  const rightPeers = peers.slice(Math.ceil(peers.length / 2));

  return (
    <article className="tn-page dp-page">
      <nav className="dp-crumbs">
        <Link to="/docs">Docs</Link>
        <span>/</span>
        <Link to="/docs">Concepts</Link>
        <span>/</span>
        <span>{clusterLabel}</span>
        <span>/</span>
        <span className="dp-crumb-current">{entity.name}</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">{clusterLabel}</span>
        <h1>{entity.name}</h1>
        <p className="dp-oneliner">{entity.oneLiner}</p>
        <div className="dp-toggle">
          <button className={mode === 'plain' ? 'on' : ''} onClick={() => setMode('plain')}>
            Plain English
          </button>
          <button className={mode === 'tech' ? 'on' : ''} onClick={() => setMode('tech')}>
            Technical
          </button>
        </div>
      </header>

      <section id="overview" className="dp-section">
        <h2>Overview</h2>
        <p className="dp-plain">
          {mode === 'plain'
            ? entity.plain
            : `${entity.name} is a record with the fields listed below. It is managed via the /${entity.id} API surface and scoped per workspace. See "Key fields" and "How it connects" for the full shape.`}
        </p>
      </section>

      <section id="connections" className="dp-section">
        <h2>How it connects</h2>
        <p className="dp-section-sub">
          This concept highlighted in context. Click a peer to navigate.
        </p>
        <div className="dp-mini-schema">
          <div className="dp-mini-layout">
            <div className="dp-mini-col">
              {leftPeers.length > 0 && <div className="dp-mini-col-label">Related</div>}
              {leftPeers.map((pid) => (
                <Link key={pid} className="dp-mini-peer" to={`/docs/concepts/${pid}`}>
                  {ENTITIES[pid].name}
                </Link>
              ))}
            </div>
            <div className="dp-mini-center">{entity.name}</div>
            <div className="dp-mini-col">
              {rightPeers.length > 0 && <div className="dp-mini-col-label">Related</div>}
              {rightPeers.map((pid) => (
                <Link key={pid} className="dp-mini-peer" to={`/docs/concepts/${pid}`}>
                  {ENTITIES[pid].name}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <ul className="dp-connects">
          {entity.connects.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      </section>

      <section id="fields" className="dp-section">
        <h2>Key fields</h2>
        <p className="dp-section-sub">
          What you&rsquo;ll see when creating or editing a {entity.name}.
        </p>
        <div className="dp-fields-table">
          {entity.fields.map(([k, v], i) => (
            <div className="dp-field-row" key={i}>
              <div className="dp-field-k">{k}</div>
              <div className="dp-field-v">{v || <span className="dp-muted">—</span>}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="example" className="dp-section">
        <h2>Example</h2>
        <div className="dp-example-card">
          <div className="dp-example-title">{entity.example.title}</div>
          <div className="dp-example-rows">
            {entity.example.lines.map(([k, v], i) => (
              <div className="dp-example-row" key={i}>
                <span>{k}</span>
                <span>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
